import React from 'react';
import { Button, CircularProgress, Stack } from '@mui/material';
import { Formik } from 'formik';
import * as yup from 'yup';
import { CssTextField } from '../../components/TextField';
import ImgUploaderInput from '../../components/ImgUploaderInput';

// Shared by the "add" and "update" activity modals. Keeping a single form here
// means the fields, validation and image handling only live in one place.
const validationSchema = yup.object().shape({
    name: yup.string().trim().required('Name is required'),
    rate: yup
        .number()
        .typeError('Enter a valid rate')
        .min(0, "Rate can't be negative")
        .required('Rate is required'),
    minimum_pax: yup
        .number()
        .typeError('Enter a number')
        .integer('Whole number only')
        .min(1, 'At least 1')
        .required('Required'),
    maximum_pax: yup
        .number()
        .typeError('Enter a number')
        .integer('Whole number only')
        .min(0, "Can't be negative")
        .test('max-gte-min', 'Max must be 0 (no cap) or ≥ minimum', function (value) {
            if (value == null || Number(value) === 0) return true;
            return Number(value) >= Number(this.parent.minimum_pax);
        }),
    description: yup.string(),
    inclusions: yup.string(),
    exclusions: yup.string(),
});

function ActivityForm({ initialValues, onSubmit, isLoading, editMode }) {
    return (
        <Formik
            onSubmit={onSubmit}
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
        >
            {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue }) => (
                <form onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                        <CssTextField
                            required
                            fullWidth
                            size="small"
                            type="text"
                            label="Name"
                            name="name"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            value={values.name}
                            error={!!touched.name && !!errors.name}
                            helperText={touched.name && errors.name}
                        />
                        <CssTextField
                            required
                            fullWidth
                            size="small"
                            type="number"
                            label="Rate (per person)"
                            name="rate"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            value={values.rate}
                            error={!!touched.rate && !!errors.rate}
                            helperText={touched.rate && errors.rate}
                            inputProps={{ min: 0 }}
                        />
                        <Stack direction="row" spacing={2}>
                            <CssTextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Minimum Pax"
                                name="minimum_pax"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.minimum_pax}
                                error={!!touched.minimum_pax && !!errors.minimum_pax}
                                helperText={touched.minimum_pax && errors.minimum_pax}
                                inputProps={{ min: 1 }}
                            />
                            <CssTextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Maximum Pax"
                                name="maximum_pax"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.maximum_pax}
                                error={!!touched.maximum_pax && !!errors.maximum_pax}
                                helperText={(touched.maximum_pax && errors.maximum_pax) || '0 = no cap'}
                                inputProps={{ min: 0 }}
                            />
                        </Stack>
                        <CssTextField
                            fullWidth
                            size="small"
                            type="text"
                            label="Description"
                            name="description"
                            multiline
                            minRows={2}
                            maxRows={4}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            value={values.description}
                            error={!!touched.description && !!errors.description}
                            helperText={touched.description && errors.description}
                        />
                        <CssTextField
                            fullWidth
                            size="small"
                            type="text"
                            label="Inclusions"
                            name="inclusions"
                            multiline
                            minRows={2}
                            maxRows={4}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            value={values.inclusions}
                            error={!!touched.inclusions && !!errors.inclusions}
                            helperText={touched.inclusions && errors.inclusions}
                        />
                        <CssTextField
                            fullWidth
                            size="small"
                            type="text"
                            label="Exclusions"
                            name="exclusions"
                            multiline
                            minRows={2}
                            maxRows={4}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            value={values.exclusions}
                            error={!!touched.exclusions && !!errors.exclusions}
                            helperText={touched.exclusions && errors.exclusions}
                        />
                        <ImgUploaderInput
                            images={[...(values.img || [])]}
                            onChange={(e) => setFieldValue('img', e)}
                        />
                        <Button
                            fullWidth
                            size="large"
                            type="submit"
                            color="secondary"
                            variant="contained"
                            disabled={isLoading}
                            sx={{ fontSize: '15px' }}
                        >
                            {isLoading ? (
                                <CircularProgress color="inherit" size={24} />
                            ) : (
                                <b>{editMode ? 'Save changes' : 'Add activity'}</b>
                            )}
                        </Button>
                    </Stack>
                </form>
            )}
        </Formik>
    );
}

export default ActivityForm;
